import { useState } from "react";
import axios from "axios";
import { Loader2, Calendar, AlertTriangle, TrendingUp, GitBranch } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// One config per kind — colors + labels + icon
const KIND_META = {
  red: {
    label: "Red-Alert",
    hint: "malefic-loaded dasha windows in the range",
    icon: AlertTriangle,
    accent: "#C15C3D",
    verdict_ok: "No red alerts fall inside this range.",
  },
  good: {
    label: "Auspicious",
    hint: "benefic + trikona-loaded windows in the range",
    icon: TrendingUp,
    accent: "#5C8A3F",
    verdict_ok: "No auspicious peaks fall inside this range.",
  },
  turning: {
    label: "Turning-Point",
    hint: "antardasha polarity shifts in the range",
    icon: GitBranch,
    accent: "#7A5AA6",
    verdict_ok: "No turning points fall inside this range.",
  },
};

function fruitionShade(pct, base) {
  if (pct >= 80) return { bg: base, text: "#FFF" };
  if (pct >= 60) return { bg: `${base}CC`, text: "#FFF" };
  if (pct >= 40) return { bg: `${base}99`, text: "#3A2A0F" };
  return { bg: `${base}55`, text: "#3A2A0F" };
}

export default function DateRangeScanner({ profileId, kind }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const today = new Date();
  const in90 = new Date(today.getTime() + 90 * 86400000);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState(iso(today));
  const [end, setEnd] = useState(iso(in90));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const scan = async () => {
    setErr(""); setBusy(true); setResult(null);
    try {
      const r = await axios.post(
        `${API}/api/profiles/${profileId}/date-range-scan`,
        { kind, start, end });
      setResult(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Scan failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="border-2 rounded-md p-4 space-y-3 bg-white/60 shadow-sm mb-4"
             style={{ borderColor: `${meta.accent}88` }}
             data-testid={`date-range-scanner-${kind}`}>
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color: meta.accent }} />
        <div className="text-[10px] uppercase tracking-[0.3em] font-mono"
             style={{ color: meta.accent }}>
          Custom date-range · {meta.label} scanner
        </div>
        <span className="text-[9px] font-mono border px-1.5 py-0.5 rounded-sm ml-auto"
              style={{ color: meta.accent, borderColor: `${meta.accent}55` }}>
          FREE · no AI
        </span>
      </div>
      <div className="text-[11px] font-mono text-[#5B4A2A]">
        Pick a range — the engine returns overlapping {meta.hint}
        with a percentage and the classical reason. Deterministic scoring —
        zero AI credits.
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513]">Start</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                 data-testid={`scanner-${kind}-start`}
                 className="bg-white/80 border border-[#B08A3E]/40 focus:border-[#B08A3E] outline-none text-[#3A2A0F] font-mono text-xs px-2 py-1.5 rounded-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513]">End</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                 data-testid={`scanner-${kind}-end`}
                 className="bg-white/80 border border-[#B08A3E]/40 focus:border-[#B08A3E] outline-none text-[#3A2A0F] font-mono text-xs px-2 py-1.5 rounded-sm" />
        </label>
        <button onClick={scan} disabled={busy}
                data-testid={`scanner-${kind}-btn`}
                className="text-white font-mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
                style={{ background: meta.accent }}>
          {busy ? <><Loader2 size={12} className="animate-spin" /> Scanning…</>
                : <><Calendar size={12} /> Scan range</>}
        </button>
      </div>
      {err && <div className="text-red-700 text-xs font-mono">{err}</div>}

      {result && result.hit_count === 0 && (
        <div className="italic text-sm text-[#5B4A2A] px-2 py-3 border border-dashed rounded-sm"
             style={{ borderColor: `${meta.accent}66` }}
             data-testid={`scanner-${kind}-empty`}>
          {meta.verdict_ok}
        </div>
      )}
      {result && result.hit_count > 0 && (
        <div className="space-y-2" data-testid={`scanner-${kind}-hits`}>
          <div className="text-[10px] uppercase tracking-widest font-mono"
               style={{ color: meta.accent }}>
            {result.hit_count} hit{result.hit_count === 1 ? "" : "s"} in range {result.range.start} → {result.range.end}
          </div>
          <div className="space-y-2">
            {result.hits.map((h, i) => {
              const shade = fruitionShade(h.percentage, meta.accent);
              return (
                <div key={`${kind}-${h.start}-${i}`}
                     data-testid={`scanner-${kind}-hit-${i}`}
                     className="rounded-sm border p-2.5 bg-white/70 flex items-center gap-3"
                     style={{ borderColor: `${meta.accent}55` }}>
                  <div className="rounded-full h-11 w-11 flex flex-col items-center justify-center shrink-0"
                       style={{ background: shade.bg, color: shade.text }}
                       title={`Percentage ${h.percentage}%`}>
                    <span className="font-heading text-sm leading-none">{h.percentage}%</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-[#3A2A0F]">
                      {h.overlap_start} → {h.overlap_end}
                      <span className="ml-2 text-[10px] text-[#8B4513]">
                        ({h.overlap_days} day{h.overlap_days === 1 ? "" : "s"} of {h.start} → {h.end})
                      </span>
                      {h.direction && (
                        <span className="ml-2 text-[9px] uppercase tracking-widest border px-1 py-0.5 rounded-sm"
                              style={{ color: meta.accent, borderColor: `${meta.accent}55` }}>
                          {h.direction}
                        </span>
                      )}
                    </div>
                    <div className="font-heading text-sm text-[#3A2A0F] mt-0.5">
                      {(h.chain || []).join(" › ")}
                    </div>
                    <div className="text-[11px] text-[#5B4A2A] italic mt-0.5">
                      {h.reason || "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
